import { useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { supplierContactLine } from '@d-orca/shared/client/logistics';
import type { IOrderSupplier } from '@d-orca/shared/client/hooks';
import type { Supplier } from '@interfaces/ILogistics';
import { PickCombo, type IPickComboOption } from '@components/ui/PickCombo/PickCombo';
import { SupplierMiniForm } from './SupplierMiniForm';
import styles from './SupplierField.module.css';

/** The escape row's value — never a real supplier id. */
const NEW_SUPPLIER = '__new';

interface ISupplierFieldProps {
  fieldId: string;
  suppliers: Supplier[];
  /** The form's supplier state (`useOrderSupplier`): the pick, and the draft
   *  the order's Save writes with it. */
  supplier: IOrderSupplier;
  /** Extra class on the combo's trigger — the form's amber "we suggested
   *  this" mark. */
  className?: string;
  /** What the form says under the pick: why it was suggested, or what was. */
  footnote?: ReactNode;
}

/**
 * The order form's optional Supplier: a filterable pick over the site's
 * suppliers (the hint line carries contact and phone, so those filter too), a
 * clear button, an "Edit supplier" link, and a "+ New supplier…" row. Both of
 * the last two swap the combo for the supplier's fields inline — no button of
 * their own: the order's Save writes the supplier with the order (spec
 * 2026-10-25 §12).
 */
export function SupplierField({ fieldId, suppliers, supplier, className, footnote }: ISupplierFieldProps) {
  const { t } = useTranslation();
  /* The combo's typed filter, mirrored so "+ New supplier…" can start the
     Company from what the user was already typing. */
  const typed = useRef<string | null>(null);
  const { picked, draft } = supplier;

  if (draft) {
    return (
      <div className={styles.field}>
        <span className={styles.label}>{t('logistics.orderDialog.supplier')}</span>
        <SupplierMiniForm supplier={supplier} draft={draft} />
      </div>
    );
  }

  const options: IPickComboOption[] = [
    ...suppliers.map((s) => ({ value: s.id, label: s.name, hint: supplierContactLine(s) ?? undefined })),
    {
      value: NEW_SUPPLIER,
      label: t('logistics.orderDialog.newSupplier'),
      pinned: true,
      keepsOpen: true,
      separatorBefore: true,
    },
  ];

  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={`${fieldId}-supplier`}>
        {t('logistics.orderDialog.supplier')}
      </label>
      <div className={styles.wrap}>
        <PickCombo
          id={`${fieldId}-supplier`}
          filterable
          className={className}
          value={picked?.id ?? ''}
          options={options}
          placeholder={t('logistics.orderDialog.noSupplier')}
          onQueryChange={(q) => {
            typed.current = q;
          }}
          onPick={(value) => {
            if (value === NEW_SUPPLIER) supplier.startNew((typed.current ?? '').trim());
            else supplier.pick(value);
          }}
        />
        {picked ? (
          <button
            type="button"
            className={styles.clear}
            aria-label={t('logistics.orderDialog.clearSupplier')}
            onClick={() => supplier.pick(null)}
          >
            ×
          </button>
        ) : null}
      </div>
      {footnote}
      {picked ? (
        <button type="button" className={styles.textLink} onClick={() => supplier.startEdit(picked)}>
          {t('logistics.orderDialog.editSupplier')}
        </button>
      ) : null}
    </div>
  );
}
